import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import i18n from 'i18next';

import { addToast } from 'core/notifications';

import axios from '@/util/axios';

import getAxiosConfig from '../helpers/getAxiosConfig';
import getOrderedLearningObjectives from '../helpers/getOrderedLearningObjectives';

const useLearningObjectives = ({
  isCourseDraft,
  objectiveableId,
  objectiveableParentId,
  objectiveableType = 'Course',
}) => {
  const queryClient = useQueryClient();

  const learningObjectivesKey = useMemo(
    () => ['learningObjectives', objectiveableId, objectiveableType],
    [objectiveableId, objectiveableType],
  );

  const learningObjectives = useQuery({
    queryKey: learningObjectivesKey,
    queryFn: async () => {
      const { data } = await axios(getAxiosConfig('get-learning-objectives', { objectiveableId, objectiveableType }));

      return {
        learningObjectives: { data: data?.data, meta: data?.meta },
        orderedLearningObjectives: getOrderedLearningObjectives({
          isCourseDraft,
          learningObjectives: data?.data,
        }),
      }
    },
    refetchOnWindowFocus: false,
  });

  const addGeneratedLearningObjective = async (newLearningObjective) => {
    const learningObjectivesData = learningObjectives.data?.learningObjectives?.data;

    if (learningObjectivesData.find(({ id }) => id === newLearningObjective.id)) {
      return;
    }

    await queryClient.setQueryData(learningObjectivesKey, (prevData) => {
      const newLearningObjectivesData = [
        ...prevData.learningObjectives.data,
        newLearningObjective,
      ];

      return {
        learningObjectives: {
          ...learningObjectives.data.learningObjectives,
          data: newLearningObjectivesData,
        },
        orderedLearningObjectives: getOrderedLearningObjectives({
          isCourseDraft,
          learningObjectives: newLearningObjectivesData,
        }),
      };
    });
  };

  const refetchLearningObjectives = () => queryClient.invalidateQueries(learningObjectivesKey);
  const generateLearningObjectives = async () => {
    await axios(getAxiosConfig('generate-learning-objectives', { objectiveableId, objectiveableParentId }));
  };


  const mutateLearningObjective = async (action, data) => {
    try {
      await axios(getAxiosConfig(action, data));
      refetchLearningObjectives();
    } catch (error) {
      const errorMessage =
        action === 'add'
          ? i18n.t('learning-objectives.modal.add-error')
          : i18n.t('learning-objectives.modal.edit-error');
      addToast(errorMessage, 'error');
    }
  };

  const deleteLearningObjective = async (data) => {
    try {
      await axios(getAxiosConfig('delete', data));
      refetchLearningObjectives();
    } catch (error) {
      addToast(i18n.t('learning-objectives.modal.delete-error'), 'error');
    }
  };

  const reorderLearningObjectives = async ({ destination, rows }) => {
    const originalOrderedLearningObjectivesData = {
      ...learningObjectives.data?.orderedLearningObjectives,
    };
    const { parentId, index: position } = destination;

    queryClient.setQueryData(learningObjectivesKey, () => ({
      ...learningObjectives.data,
      orderedLearningObjectives: rows,
    }));

    const id = rows.items[parentId].children[position];

    try {
      await axios(getAxiosConfig('move-learning-objective', { id, objectiveableId, position }));
    } catch (error) {
      addToast(i18n.t('oops-wrong'), 'error');
      queryClient.setQueryData(learningObjectivesKey, () => ({
        ...learningObjectives.data,
        orderedLearningObjectives: originalOrderedLearningObjectivesData,
      }));
    }
  };

  const toggleVisibility = async (value) => {
    try {
      await axios(
        getAxiosConfig('toggle-visibility', {
          id: objectiveableId,
          display_learning_objectives: value,
        }),
      );
      refetchLearningObjectives();
    } catch {
      addToast(i18n.t('learning-objectives.toggle-visibility-error'), 'error');
    }
  };

  return {
    addGeneratedLearningObjective,
    deleteLearningObjective,
    generateLearningObjectives,
    learningObjectives,
    mutateLearningObjective,
    refetchLearningObjectives,
    reorderLearningObjectives,
    toggleVisibility,
    orderedLearningObjectives: learningObjectives.data?.orderedLearningObjectives,
  };
};

export default useLearningObjectives;
